import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import { Menu, MenuItem } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { titleCase } from "title-case";
import { BankAccount } from "hooks/useGetBankAccounts";

export const ALL_ACCOUNTS_ID = "allAccounts";

const MENU_ID = "account-filter-menu";

interface AccountFilterButtonProps {
  accounts: BankAccount[];
  /** Selected account id, or `ALL_ACCOUNTS_ID` for no account scope. */
  value: string;
  onChange: (accountId: string) => void;
}

/**
 * The ledger's account scope, as a button rather than a form field: it acts on
 * the table beside it instead of collecting a value for a form.
 *
 * An account nickname is runtime data and must not go through a bare `t()`:
 * i18next reads ":" as a namespace separator and would render "Ops: West" as
 * " West" (DS_MIGRATION.md). Only the sentinel row translates.
 */
const AccountFilterButton: React.FC<AccountFilterButtonProps> = ({
  accounts,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const accountLabel = (account: BankAccount) =>
    titleCase((account.nickname || account.bankName || "").toLowerCase()) ||
    t("Account");

  const selected = accounts.find((account) => account.id === value);
  const label = selected ? accountLabel(selected) : t("All Accounts");

  const pick = (accountId: string) => () => {
    onChange(accountId);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="primary-outlined"
        // The name carries the selection, because a bare `aria-label` REPLACES
        // the visible text and would announce the purpose while hiding which
        // account is scoped, the one thing this control says.
        aria-label={t("accountFilterLabel", { account: label })}
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        aria-controls={anchorEl ? MENU_ID : undefined}
        endIcon={<HugeiconsIcon icon={ArrowDown01Icon} size={16} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {label}
      </Button>

      <Menu
        id={MENU_ID}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          selected={value === ALL_ACCOUNTS_ID}
          onClick={pick(ALL_ACCOUNTS_ID)}
        >
          {t("All Accounts")}
        </MenuItem>
        {accounts.map((account) => (
          <MenuItem
            key={account.id}
            selected={account.id === value}
            onClick={pick(account.id)}
          >
            {accountLabel(account)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default AccountFilterButton;
